import { Fragment, memo, useLayoutEffect } from 'react';
import { isEqual } from 'lodash';
import { useDragLayer, XYCoord } from 'react-dnd';
import { ILayout, useEditorStore } from '@diana/core';
import {
  FieldTypeComponent,
  ContainerTypeComponent,
  IDndLayoutComponentLocation as ILocation,
  IDndLayoutComponentDragObject as IDragObject,
} from '../../../../../providers/DndLayoutComponents2';
import { getPlaceholdersInLayout } from './Support';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';

export const DndSentinel = memo(() => {
  const getFlow = useEditorStore((s) => s.getFlow);
  const getLayoutById = useEditorStore((s) => s.getLayoutById);

  const getLocation = useFlowBuilder2PageStore((s) => s.getLayoutDndPlaceholderLocation);
  const setLocation = useFlowBuilder2PageStore((s) => s.setLayoutDndPlaceholderLocation);
  const dragItem = useFlowBuilder2PageStore((s) => s.layoutDndDragItem);
  const setDragItem = useFlowBuilder2PageStore((s) => s.setLayoutDndDragItem);

  const rawDragItem = useDragLayer<IDragObject | null, IDragObject>((monitor) => {
    if (monitor.isDragging()) {
      const itemType = monitor.getItemType();
      if (itemType === FieldTypeComponent || itemType === ContainerTypeComponent) {
        return monitor.getItem();
      }
    }
    return null;
  });
  const clientOffset = useDragLayer<XYCoord | null, IDragObject>((monitor) =>
    monitor.getClientOffset(),
  );

  useLayoutEffect(() => {
    if (rawDragItem) setTimeout(() => setDragItem(rawDragItem));
    else setDragItem(null);
  }, [rawDragItem, setDragItem]);

  useLayoutEffect(() => {
    const flow = getFlow();
    if (flow && dragItem && clientOffset) {
      const layouts = Object.values(
        flow.steps.reduce<Record<string, ILayout | null>>((result, step) => {
          return { ...result, [step.layoutId]: getLayoutById(step.layoutId) };
        }, {}),
      );

      const { location, distance, sibling, exists } = layouts.reduce<{
        location: ILocation | null;
        distance: number;
        sibling: boolean;
        exists: boolean;
      }>(
        (result, layout) => {
          if (layout) {
            const exists = layout.components.some((c) => c.id === dragItem.component.id);
            if (exists) result.exists = true;

            const placeholders = getPlaceholdersInLayout(layout, dragItem.component, clientOffset);

            for (const { distance, parentId, index, isSibling } of placeholders) {
              if (!result.location || (result.location && distance < result.distance)) {
                result.location = { layoutId: layout.id, parentId, index };
                result.distance = distance;
                result.sibling = isSibling;
              }
            }
          }

          return result;
        },
        { location: null, distance: 0, sibling: false, exists: false },
      );

      if (sibling || !location) setLocation(null);
      else if (!isEqual(getLocation(), location)) {
        if (exists && distance > 4) setLocation(null);
        else setLocation(location);
      }
    }
  }, [getFlow, dragItem, clientOffset, setLocation, getLocation, getLayoutById]);

  return <Fragment />;
}, isEqual);
